/*
 * Pure helpers for the Network workspace: fallback mapping, filters, time, and the
 * analyst-facing readings of the analytics (vulnerabilities, position lines, introductions,
 * AI context digest, suggested questions). No React, no d3 — unit tested.
 */
import type { GraphData } from '../../../services/graphService';
import {
  Community,
  NetworkAnalysis,
  NodeMetrics,
  ROLE_LABEL,
  StructuralRole,
  analyzeNetwork,
  hopDistances,
} from '../graphAnalytics';
import type { ConnectionLead, EntityType, LinkKind, NetworkGraph, NetworkLink, NetworkNode, NodeRole, RelationKind } from '../types';

/* ------------------------------------------------------------------ */
/* Labels                                                              */
/* ------------------------------------------------------------------ */

export const KIND_LABEL: Record<LinkKind, string> = {
  employment: 'Employment',
  hierarchy: 'Hierarchy',
  co_affiliation: 'Co-affiliation',
  relationship: 'Relationships',
};

export const KIND_ORDER: LinkKind[] = ['relationship', 'employment', 'hierarchy', 'co_affiliation'];

export const TYPE_LABEL: Record<RelationKind, string> = {
  os: 'Org – Person',
  oo: 'Org – Org',
  ss: 'Person – Person',
};

export type Lens = 'map' | 'focus' | 'clusters';

export const LENS_LABEL: Record<Lens, string> = { map: 'Map', focus: 'Focus', clusters: 'Clusters' };

/* ------------------------------------------------------------------ */
/* Fallback: legacy /api/relationships/graph → NetworkGraph            */
/* ------------------------------------------------------------------ */

const refId = (ref: any): string => (ref && typeof ref === 'object' ? ref.id : ref);
const clampWeight = (w: unknown, fallback: number) => {
  const n = Number(w);
  return Number.isFinite(n) && n > 0 ? Math.max(1, Math.min(5, Math.round(n))) : fallback;
};

export function inferRelationKind(a: EntityType, b: EntityType): RelationKind {
  if (a === 'organization' && b === 'organization') return 'oo';
  if (a === 'stakeholder' && b === 'stakeholder') return 'ss';
  return 'os';
}

/** Maps the older graph endpoint (no roles, no influence, no dates) to the network contract */
export function fromLegacyGraph(data: GraphData): NetworkGraph {
  const nodes: NetworkNode[] = (data.nodes || []).map((n) => ({ id: n.id, type: n.type, label: n.label, status: 'approved' }));
  const seen = new Set<string>();
  const links: NetworkLink[] = [];
  (data.links || []).forEach((l: any, i) => {
    const source = refId(l.source);
    const target = refId(l.target);
    const type: RelationKind = l.type === 'oo' || l.type === 'ss' ? l.type : 'os';
    let link: NetworkLink;
    if (l.custom) {
      link = {
        id: `rel:${l.id || i}`,
        source,
        target,
        kind: 'relationship',
        type,
        label: l.label ?? null,
        category: l.category ?? null,
        description: l.description ?? null,
        weight: clampWeight(l.weight, 2),
        isCurrent: true,
        relationshipId: l.id ?? null,
      };
    } else if (type === 'os') {
      link = {
        id: `asg:legacy-${i}`,
        source,
        target,
        kind: 'employment',
        type,
        label: l.label ?? null,
        weight: 3,
        // the older endpoint does not say whether an assignment has ended
        isCurrent: true,
      };
    } else if (type === 'oo') {
      link = { id: `org:${target}`, source, target, kind: 'hierarchy', type, weight: 4, isCurrent: true };
    } else {
      const [a, b] = source < target ? [source, target] : [target, source];
      link = { id: `co:${a}|${b}`, source: a, target: b, kind: 'co_affiliation', type, weight: clampWeight(l.weight, 1), isCurrent: true };
    }
    if (seen.has(link.id)) link = { ...link, id: `${link.id}#${i}` };
    seen.add(link.id);
    links.push(link);
  });
  return { nodes, links, generatedAt: new Date().toISOString() };
}

/* ------------------------------------------------------------------ */
/* Filters                                                             */
/* ------------------------------------------------------------------ */

export interface NetworkFilters {
  entities: 'all' | EntityType;
  kinds: Record<LinkKind, boolean>;
  types: Record<RelationKind, boolean>;
  /** Include ties that have ended */
  includePast: boolean;
  hideIsolated: boolean;
}

export const DEFAULT_FILTERS: NetworkFilters = {
  entities: 'all',
  kinds: { employment: true, hierarchy: true, co_affiliation: true, relationship: true },
  types: { os: true, oo: true, ss: true },
  includePast: false,
  hideIsolated: false,
};

export function activeFilterCount(f: NetworkFilters): number {
  return (
    (f.entities !== 'all' ? 1 : 0) +
    Object.values(f.kinds).filter((v) => !v).length +
    Object.values(f.types).filter((v) => !v).length +
    (f.includePast ? 1 : 0) +
    (f.hideIsolated ? 1 : 0)
  );
}

export interface NetworkView {
  nodes: NetworkNode[];
  links: NetworkLink[];
  nodesById: Map<string, NetworkNode>;
}

/**
 * Nodes and links in scope. `includePast` keeps ended ties (time mode needs them);
 * the time slider then narrows links further with `linksActiveAt`.
 */
export function filterGraph(graph: NetworkGraph, filters: NetworkFilters, options: { keepPast?: boolean } = {}): NetworkView {
  const keepPast = filters.includePast || options.keepPast;
  let nodes = graph.nodes.filter((n) => filters.entities === 'all' || n.type === filters.entities);
  const ids = new Set(nodes.map((n) => n.id));
  const links = graph.links.filter(
    (l) =>
      l.source !== l.target &&
      ids.has(l.source) &&
      ids.has(l.target) &&
      filters.kinds[l.kind] !== false &&
      filters.types[l.type] !== false &&
      (keepPast || l.isCurrent !== false),
  );
  const linked = new Set<string>();
  links.forEach((l) => {
    linked.add(l.source);
    linked.add(l.target);
  });
  if (filters.hideIsolated) {
    nodes = nodes.filter((n) => linked.has(n.id));
  } else if (!keepPast) {
    // Somebody whose every tie has ended belongs to the past view, not this
    // one: showing them as a loose dot would say they are still in place.
    // Entities that have never had a tie stay — they are simply unconnected.
    const hadTies = new Set<string>();
    graph.links.forEach((l) => {
      hadTies.add(l.source);
      hadTies.add(l.target);
    });
    // …and one carrying an assessed influence score stands on its own
    nodes = nodes.filter((n) => linked.has(n.id) || !hadTies.has(n.id) || !!n.influence);
  }
  return { nodes, links, nodesById: new Map(graph.nodes.map((n) => [n.id, n])) };
}

export const toEdges = (links: NetworkLink[]) => links.map((l) => ({ source: l.source, target: l.target, weight: l.weight }));

export const analyzeView = (nodes: NetworkNode[], links: NetworkLink[]): NetworkAnalysis =>
  analyzeNetwork(
    nodes.map((n) => n.id),
    toEdges(links),
  );

/* ------------------------------------------------------------------ */
/* Time                                                                */
/* ------------------------------------------------------------------ */

export const DAY = 86400000;

export function parseDate(value?: string | null): number | null {
  if (!value) return null;
  const s = String(value).trim();
  const m = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/.exec(s);
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), m[2] ? Number(m[2]) - 1 : 0, m[3] ? Number(m[3]) : 1);
  return Number.isFinite(t) ? t : null;
}

export type Interval = [number, number];

const roleInterval = (r: NodeRole): Interval => [parseDate(r.startDate) ?? -Infinity, parseDate(r.endDate) ?? Infinity];

/**
 * When a tie exists: dated ties use their dates; undated ties are treated as always present
 * (unknown start). Co-affiliations are active while both people hold a role at a shared organization.
 */
export function linkIntervals(link: NetworkLink, nodesById: Map<string, NetworkNode>): Interval[] {
  if (link.kind === 'co_affiliation' && link.sharedOrganizationIds?.length) {
    const a = nodesById.get(link.source)?.roles;
    const b = nodesById.get(link.target)?.roles;
    if (a?.length && b?.length) {
      const out: Interval[] = [];
      for (const orgId of link.sharedOrganizationIds) {
        for (const ra of a.filter((r) => r.organizationId === orgId)) {
          for (const rb of b.filter((r) => r.organizationId === orgId)) {
            const [s1, e1] = roleInterval(ra);
            const [s2, e2] = roleInterval(rb);
            const s = Math.max(s1, s2);
            const e = Math.min(e1, e2);
            if (s <= e) out.push([s, e]);
          }
        }
      }
      if (out.length) return out;
    }
  }
  const start = parseDate(link.startDate) ?? -Infinity;
  const end = parseDate(link.endDate) ?? Infinity;
  return [[start, Math.max(start, end)]];
}

export const isActiveAt = (intervals: Interval[], t: number) => intervals.some(([s, e]) => s <= t && t <= e);

export function intervalsFor(links: NetworkLink[], nodesById: Map<string, NetworkNode>): Map<string, Interval[]> {
  return new Map(links.map((l) => [l.id, linkIntervals(l, nodesById)]));
}

export function linksActiveAt(links: NetworkLink[], intervals: Map<string, Interval[]>, t: number): NetworkLink[] {
  return links.filter((l) => isActiveAt(intervals.get(l.id) || [[-Infinity, Infinity]], t));
}

/** Earliest dated tie → today, or null when nothing is dated */
export function timeExtent(intervals: Map<string, Interval[]>, now = Date.now()): { min: number; max: number } | null {
  let min = Infinity;
  intervals.forEach((list) =>
    list.forEach(([s, e]) => {
      if (Number.isFinite(s)) min = Math.min(min, s);
      if (Number.isFinite(e)) min = Math.min(min, e);
    }),
  );
  if (!Number.isFinite(min)) return null;
  const max = Math.max(now, min + 30 * DAY);
  return { min: Math.min(min, max - 365 * DAY), max };
}

/** Tie starts and ends per bucket, for the scrubber's sparkline */
export function timeHistogram(intervals: Map<string, Interval[]>, extent: { min: number; max: number }, buckets = 48) {
  const starts = new Array(buckets).fill(0);
  const ends = new Array(buckets).fill(0);
  const span = extent.max - extent.min || 1;
  const bucketOf = (t: number) => Math.max(0, Math.min(buckets - 1, Math.floor(((t - extent.min) / span) * buckets)));
  intervals.forEach((list) =>
    list.forEach(([s, e]) => {
      if (Number.isFinite(s)) starts[bucketOf(s)]++;
      if (Number.isFinite(e) && e <= extent.max) ends[bucketOf(e)]++;
    }),
  );
  return { starts, ends };
}

export const monthBucket = (t: number) => {
  const d = new Date(t);
  return d.getUTCFullYear() * 12 + d.getUTCMonth();
};

export const formatMonth = (t: number) =>
  new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', timeZone: 'UTC' });

const year = (value?: string | null) => (value ? String(value).slice(0, 4) : '');

/** "2019–now", "2015–2018", "since 2019", "until 2018", "" */
export function formatSpan(start?: string | null, end?: string | null, isCurrent?: boolean): string {
  const s = year(start);
  const e = year(end);
  if (s && e) return s === e ? s : `${s}–${e}`;
  if (s) return isCurrent === false ? `${s}–?` : `${s}–now`;
  if (e) return `until ${e}`;
  return '';
}

/* ------------------------------------------------------------------ */
/* Entities                                                            */
/* ------------------------------------------------------------------ */

export function currentRole(node?: NetworkNode | null): NodeRole | null {
  if (!node?.roles?.length) return null;
  return node.roles.find((r) => r.isCurrent) || null;
}

/** The last post somebody held, for when they hold none today */
export function lastRole(node?: NetworkNode | null): NodeRole | null {
  if (!node?.roles?.length) return null;
  const past = node.roles.filter((r) => !r.isCurrent);
  if (!past.length) return null;
  return [...past].sort(
    (a, b) =>
      (b.endDate ?? '').localeCompare(a.endDate ?? '') ||
      (b.startDate ?? '').localeCompare(a.startDate ?? ''),
  )[0];
}

/** Current posts first, then the ones held before, most recent first */
export function careerOf(node?: NetworkNode | null): NodeRole[] {
  if (!node?.roles?.length) return [];
  return [...node.roles].sort(
    (a, b) =>
      Number(b.isCurrent) - Number(a.isCurrent) ||
      (b.endDate ?? '').localeCompare(a.endDate ?? '') ||
      (b.startDate ?? '').localeCompare(a.startDate ?? '') ||
      a.title.localeCompare(b.title),
  );
}

/** "Director · Ministry of Mines" for people; "Ministry · Conakry" for organizations */
export function roleLine(node?: NetworkNode | null, labelOf?: (domain: 'stakeholder_category' | 'organization_type', key?: string | null) => string | undefined): string {
  if (!node) return '';
  if (node.type === 'stakeholder') {
    const role = currentRole(node);
    if (role) return [role.title, role.organizationName].filter(Boolean).join(' · ');
    // Somebody who has left office is still the person who held it: saying
    // "Person" of a former minister throws away what the directory knows
    const former = lastRole(node);
    if (former) {
      const held = [former.title, former.organizationName].filter(Boolean).join(' · ');
      return `Formerly ${held}`;
    }
    const category = node.category ? labelOf?.('stakeholder_category', node.category) || node.category : '';
    return category || 'Person';
  }
  const type = node.orgType ? labelOf?.('organization_type', node.orgType) || node.orgType : '';
  return [type || 'Organization', node.location].filter(Boolean).join(' · ');
}

export const topPercent = (pct: number) => `Top ${Math.max(1, Math.min(100, Math.round(100 - pct)))}%`;

/** "Top 4%" for the upper half, "Bottom 15%" below it — never "Top 85%" */
export const rankLabel = (pct: number) => (pct >= 50 ? topPercent(pct) : `Bottom ${Math.max(1, Math.round(pct))}%`);

export function modularityLabel(q: number): 'Strong' | 'Clear' | 'Weak' | 'None' {
  if (q >= 0.5) return 'Strong';
  if (q >= 0.3) return 'Clear';
  if (q >= 0.15) return 'Weak';
  return 'None';
}

/** A cluster is named after its most influential organization, else its most influential member */
export function clusterNames(analysis: NetworkAnalysis, nodesById: Map<string, NetworkNode>): Map<number, string> {
  const names = new Map<number, string>();
  const used = new Map<string, number>();
  analysis.communities.forEach((c) => {
    const byRank = c.members
      .slice()
      .sort((a, b) => (analysis.metrics.get(b)?.pagerank || 0) - (analysis.metrics.get(a)?.pagerank || 0));
    const org = byRank.find((id) => nodesById.get(id)?.type === 'organization');
    let name = nodesById.get(org || byRank[0])?.label || `Cluster ${c.id + 1}`;
    const count = used.get(name) || 0;
    used.set(name, count + 1);
    if (count) name = `${name} (${count + 1})`;
    names.set(c.id, name);
  });
  return names;
}

export const communityOf = (analysis: NetworkAnalysis, id: string): Community | undefined => {
  const m = analysis.metrics.get(id);
  return m ? analysis.communities.find((c) => c.id === m.community) : undefined;
};

/* ------------------------------------------------------------------ */
/* Vulnerabilities                                                     */
/* ------------------------------------------------------------------ */

export interface Gatekeeper {
  id: string;
  /** Entities that would lose their connection to the rest of their component */
  cutOff: number;
}

/** Articulation points with how many entities they would cut off, most critical first */
export function gatekeepers(analysis: NetworkAnalysis): Gatekeeper[] {
  const g = analysis.graph;
  return analysis.articulation
    .map((id) => {
      const v = g.index.get(id)!;
      // Pieces of the original component once v is gone
      const members = new Set(hopDistances(g, v).keys());
      const componentSize = members.size;
      const seen = new Set<number>([v]);
      let largest = 0;
      members.forEach((start) => {
        if (seen.has(start)) return;
        let size = 0;
        const stack = [start];
        seen.add(start);
        while (stack.length) {
          const x = stack.pop()!;
          size++;
          for (const w of g.nbrs[x]) {
            if (!seen.has(w)) {
              seen.add(w);
              stack.push(w);
            }
          }
        }
        largest = Math.max(largest, size);
      });
      return { id, cutOff: Math.max(0, componentSize - 1 - largest) };
    })
    .sort((a, b) => b.cutOff - a.cutOff || (analysis.metrics.get(b.id)?.betweenness || 0) - (analysis.metrics.get(a.id)?.betweenness || 0));
}

export interface BridgeTie {
  a: string;
  b: string;
  /** size of the side containing a / b once the tie is cut */
  sizeA: number;
  sizeB: number;
}

/** Ties whose loss splits the network; ties between two real groups first, lone leaves last */
export function bridgeTies(analysis: NetworkAnalysis): BridgeTie[] {
  const g = analysis.graph;
  return analysis.bridges
    .map(([a, b]) => {
      const ia = g.index.get(a)!;
      const ib = g.index.get(b)!;
      const sideA = hopDistancesBlocked(analysis, ia, [ia, ib]);
      const total = hopDistances(g, ia).size;
      return { a, b, sizeA: sideA, sizeB: total - sideA };
    })
    .sort((x, y) => Math.min(y.sizeA, y.sizeB) - Math.min(x.sizeA, x.sizeB));
}

function hopDistancesBlocked(analysis: NetworkAnalysis, start: number, edge: [number, number]): number {
  const g = analysis.graph;
  const seen = new Set<number>([start]);
  const stack = [start];
  while (stack.length) {
    const v = stack.pop()!;
    for (const w of g.nbrs[v]) {
      if (seen.has(w)) continue;
      if ((v === edge[0] && w === edge[1]) || (v === edge[1] && w === edge[0])) continue;
      seen.add(w);
      stack.push(w);
    }
  }
  return seen.size;
}

export interface Vulnerabilities {
  gatekeepers: Gatekeeper[];
  bridges: BridgeTie[];
  isolates: string[];
  /** stakeholders with roles data but no current role */
  withoutRole: string[];
  /** entities without any influence score (only when the server provides influence) */
  unscored: string[];
  hasRoleData: boolean;
  hasInfluenceData: boolean;
}

export function vulnerabilities(analysis: NetworkAnalysis, nodes: NetworkNode[]): Vulnerabilities {
  const hasRoleData = nodes.some((n) => n.roles !== undefined);
  const hasInfluenceData = nodes.some((n) => n.influence !== undefined);
  return {
    // an articulation point that only strands one leaf is not a meaningful single point of failure
    gatekeepers: gatekeepers(analysis).filter((g) => g.cutOff >= 2),
    bridges: bridgeTies(analysis),
    isolates: analysis.isolates,
    withoutRole: hasRoleData ? nodes.filter((n) => n.type === 'stakeholder' && !currentRole(n)).map((n) => n.id) : [],
    unscored: hasInfluenceData ? nodes.filter((n) => !n.influence?.scores?.length).map((n) => n.id) : [],
    hasRoleData,
    hasInfluenceData,
  };
}

/* ------------------------------------------------------------------ */
/* Dossier readings                                                    */
/* ------------------------------------------------------------------ */

/** Other clusters an entity is tied into, by number of ties */
export function bridgedClusters(analysis: NetworkAnalysis, id: string): Array<{ community: number; ties: number }> {
  const g = analysis.graph;
  const i = g.index.get(id);
  const own = analysis.metrics.get(id)?.community;
  if (i === undefined) return [];
  const counts = new Map<number, number>();
  for (const j of g.nbrs[i]) {
    const c = analysis.metrics.get(g.ids[j])?.community;
    if (c === undefined || c === own) continue;
    if (!analysis.communities.some((k) => k.id === c)) continue;
    counts.set(c, (counts.get(c) || 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([community, ties]) => ({ community, ties }))
    .sort((a, b) => b.ties - a.ties);
}

/** One plain-language line on where an entity sits in the network */
export function positionSummary(
  id: string,
  analysis: NetworkAnalysis,
  names: Map<number, string>,
  gatekeeperList: Gatekeeper[] = [],
): string {
  const m = analysis.metrics.get(id);
  if (!m) return 'Not in the current view';
  const own = names.get(m.community);
  const bridged = bridgedClusters(analysis, id)
    .map((b) => names.get(b.community))
    .filter(Boolean) as string[];
  const ties = `${m.degree} ${m.degree === 1 ? 'tie' : 'ties'}`;
  switch (m.role) {
    case 'isolate':
      return 'No ties in this view';
    case 'gatekeeper': {
      const cut = gatekeeperList.find((g) => g.id === id)?.cutOff;
      return cut
        ? `Single point of access — ${cut} ${cut === 1 ? 'entity relies' : 'entities rely'} on this actor to reach the rest`
        : `${topPercent(m.pct.brokerage)} broker and a single point of access`;
    }
    case 'broker':
      if (own && bridged.length) return `${topPercent(m.pct.brokerage)} broker — connects ${own} with ${bridged.slice(0, 2).join(' and ')}`;
      return `${topPercent(m.pct.brokerage)} broker — sits between otherwise separate contacts`;
    case 'hub': {
      const strongTies = analysis.graph.nbrs[analysis.graph.index.get(id)!]
        .map((j) => analysis.metrics.get(analysis.graph.ids[j]))
        .filter((n) => n && n.pct.influence >= 75).length;
      return `${topPercent(m.pct.influence)} by influence — ${ties}${strongTies ? `, ${strongTies} to well-connected actors` : ''}`;
    }
    case 'connector':
      return `${ties}${own ? `, mostly inside ${own}` : ''}${bridged.length ? ` · reaches into ${bridged[0]}` : ''}`;
    default:
      return `${ties} — at the edge of ${own || 'the network'}`;
  }
}

export interface TieItem {
  link: NetworkLink;
  otherId: string;
}

/** An entity's ties grouped by kind, strongest and current first */
export function groupTies(id: string, links: NetworkLink[]): Array<{ kind: LinkKind; items: TieItem[] }> {
  const groups = new Map<LinkKind, TieItem[]>();
  links.forEach((link) => {
    if (link.source !== id && link.target !== id) return;
    const list = groups.get(link.kind) || [];
    list.push({ link, otherId: link.source === id ? link.target : link.source });
    groups.set(link.kind, list);
  });
  return KIND_ORDER.filter((k) => groups.has(k)).map((kind) => ({
    kind,
    items: groups
      .get(kind)!
      .sort((a, b) => Number(b.link.isCurrent) - Number(a.link.isCurrent) || b.link.weight - a.link.weight),
  }));
}

export interface Introduction {
  id: string;
  /** direct contacts who can make the introduction, strongest tie first */
  via: string[];
}

/** Most influential second-degree contacts, with who could introduce them */
export function introductions(analysis: NetworkAnalysis, id: string, limit = 5): Introduction[] {
  const g = analysis.graph;
  const i = g.index.get(id);
  if (i === undefined) return [];
  const direct = new Set(g.nbrs[i]);
  const second = new Map<number, Map<number, number>>();
  direct.forEach((j) => {
    g.adj[j].forEach((w, k) => {
      if (k === i || direct.has(k)) return;
      const via = second.get(k) || new Map<number, number>();
      via.set(j, Math.max(via.get(j) || 0, w + (g.adj[i].get(j) || 0)));
      second.set(k, via);
    });
  });
  return Array.from(second.entries())
    .map(([k, via]) => ({
      id: g.ids[k],
      rank: analysis.metrics.get(g.ids[k])?.pagerank || 0,
      via: Array.from(via.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([j]) => g.ids[j]),
    }))
    .sort((a, b) => b.rank - a.rank)
    .slice(0, limit)
    .map(({ id: target, via }) => ({ id: target, via }));
}

export function reachCounts(analysis: NetworkAnalysis, id: string): { direct: number; twoHops: number; share: number } {
  const g = analysis.graph;
  const i = g.index.get(id);
  if (i === undefined) return { direct: 0, twoHops: 0, share: 0 };
  const dist = hopDistances(g, i, 2);
  const twoHops = dist.size - 1;
  return { direct: g.nbrs[i].length, twoHops, share: g.ids.length > 1 ? twoHops / (g.ids.length - 1) : 0 };
}

/* ------------------------------------------------------------------ */
/* Leads                                                               */
/* ------------------------------------------------------------------ */

/** Pending connection leads that can be drawn: both ends visible, no duplicate pair */
export function drawableLeads(leads: ConnectionLead[], visibleIds: Set<string>): ConnectionLead[] {
  const seen = new Set<string>();
  return leads.filter((lead) => {
    if (lead.kind !== 'connection' || lead.status !== 'pending' || !lead.targetId) return false;
    if (!visibleIds.has(lead.subjectId) || !visibleIds.has(lead.targetId)) return false;
    const key = [lead.subjectId, lead.targetId].sort().join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/* ------------------------------------------------------------------ */
/* AI context digest                                                   */
/* ------------------------------------------------------------------ */

export interface DigestInput {
  view: NetworkView;
  links: NetworkLink[];
  analysis: NetworkAnalysis;
  names: Map<number, string>;
  vulns: Vulnerabilities;
  filters: NetworkFilters;
  lens: Lens;
  selectedId?: string | null;
  compareId?: string | null;
  time?: number | null;
  paths?: string[][];
  limit?: number;
}

const pctText = (m?: NodeMetrics) =>
  m ? `influence p${m.pct.influence}, brokerage p${m.pct.brokerage}, ${m.degree} ties, ${ROLE_LABEL[m.role]}` : '';

/**
 * Compact analytics digest for ask/brief (≤ 12,000 chars). Entities are written as
 * `Name [uuid]`; the server maps the ids to its own references.
 */
export function buildNetworkContext(input: DigestInput): string {
  const { view, links, analysis, names, vulns, filters, lens, selectedId, compareId, time, paths, limit = 12000 } = input;
  const node = (id: string) => view.nodesById.get(id);
  const ent = (id: string) => `${node(id)?.label || 'Unknown'} [${id}]`;
  const who = (id: string) => {
    const n = node(id);
    const line = roleLine(n);
    return `${ent(id)}${line ? ` — ${line}` : ''}`;
  };
  const people = view.nodes.filter((n) => n.type === 'stakeholder').length;
  const lines: string[] = [];
  const push = (s = '') => lines.push(s);

  push('# Network view (Staketory workspace)');
  const hiddenKinds = (Object.keys(filters.kinds) as LinkKind[]).filter((k) => !filters.kinds[k]).map((k) => KIND_LABEL[k]);
  const hiddenTypes = (Object.keys(filters.types) as RelationKind[]).filter((k) => !filters.types[k]).map((k) => TYPE_LABEL[k]);
  push(
    `Scope: ${view.nodes.length} entities (${people} people, ${view.nodes.length - people} organizations), ${links.length} ties` +
      `${filters.entities !== 'all' ? `; only ${filters.entities === 'stakeholder' ? 'people' : 'organizations'}` : ''}` +
      `${hiddenKinds.length ? `; hidden tie kinds: ${hiddenKinds.join(', ')}` : ''}` +
      `${hiddenTypes.length ? `; hidden tie types: ${hiddenTypes.join(', ')}` : ''}` +
      `${filters.includePast ? '; includes past ties' : '; current ties only'}` +
      `${time ? `; as of ${new Date(time).toISOString().slice(0, 7)}` : ''}; lens: ${LENS_LABEL[lens]}`,
  );
  push(
    `Structure: density ${(analysis.density * 100).toFixed(1)}%, ${analysis.communities.length} clusters (clustering strength ${modularityLabel(analysis.modularity)}, modularity ${analysis.modularity.toFixed(2)}), ` +
      `${analysis.components} connected groups, largest holds ${Math.round(analysis.largestComponentShare * 100)}% of entities`,
  );

  const focus = [selectedId, compareId].filter(Boolean) as string[];
  if (focus.length) {
    push();
    push('## Analyst focus');
    focus.forEach((id, i) => {
      const m = analysis.metrics.get(id);
      push(`${i === 0 ? 'Selected' : 'Compared with'}: ${who(id)} (${pctText(m)}${m ? `, cluster ${names.get(m.community) || 'none'}` : ''})`);
    });
  }
  if (paths?.length) {
    push(`Paths shown: ${paths.map((p) => p.map((id) => node(id)?.label || id).join(' → ')).join(' | ')}`);
  }

  const ranking = (title: string, ids: string[], extra?: (id: string) => string) => {
    if (!ids.length) return;
    push();
    push(`## ${title}`);
    ids.slice(0, 15).forEach((id, i) => {
      const m = analysis.metrics.get(id);
      push(`${i + 1}. ${who(id)} (${pctText(m)}${extra ? `, ${extra(id)}` : ''})`);
    });
  };
  ranking('Key actors by network influence (PageRank)', analysis.ranked.influence);
  ranking('Brokers (betweenness)', analysis.ranked.brokerage);
  ranking('Connectors (most direct ties)', analysis.ranked.degree);
  if (vulns.gatekeepers.length) {
    push();
    push('## Gatekeepers (single points of failure)');
    vulns.gatekeepers.slice(0, 15).forEach((g) => push(`- ${who(g.id)} — removing them cuts off ${g.cutOff} entities`));
  }

  if (analysis.communities.length) {
    push();
    push('## Clusters');
    analysis.communities.slice(0, 15).forEach((c) => {
      push(
        `- ${names.get(c.id)}: ${c.members.length} members, cohesion ${(c.cohesion * 100).toFixed(0)}%, ${c.externalTies} outside ties; leaders ${c.leaders.map(ent).join(', ')}`,
      );
    });
  }

  if (vulns.bridges.length) {
    push();
    push('## Fragile ties (bridges)');
    vulns.bridges.slice(0, 12).forEach((b) => push(`- ${ent(b.a)} — ${ent(b.b)} (splits ${b.sizeA} / ${b.sizeB})`));
  }

  const gaps: string[] = [];
  if (vulns.isolates.length) gaps.push(`${vulns.isolates.length} isolated entities: ${vulns.isolates.slice(0, 12).map(ent).join(', ')}`);
  if (vulns.withoutRole.length) gaps.push(`${vulns.withoutRole.length} people without a current role: ${vulns.withoutRole.slice(0, 12).map(ent).join(', ')}`);
  if (vulns.unscored.length) gaps.push(`${vulns.unscored.length} entities without influence scores`);
  if (gaps.length) {
    push();
    push('## Gaps');
    gaps.forEach((g) => push(`- ${g}`));
  }

  let text = lines.join('\n');
  if (text.length > limit) text = `${text.slice(0, limit - 2)}\n…`;
  return text;
}

/* ------------------------------------------------------------------ */
/* Suggested questions                                                 */
/* ------------------------------------------------------------------ */

export function buildSuggestions(input: {
  analysis: NetworkAnalysis;
  nodesById: Map<string, NetworkNode>;
  names: Map<number, string>;
  vulns: Vulnerabilities;
  selectedId?: string | null;
  max?: number;
}): string[] {
  const { analysis, nodesById, names, vulns, selectedId, max = 4 } = input;
  const label = (id?: string) => (id ? nodesById.get(id)?.label : undefined);
  const out: string[] = [];
  const add = (q?: string | false | null) => {
    if (q && !out.includes(q)) out.push(q);
  };

  if (selectedId && analysis.metrics.has(selectedId)) {
    const name = label(selectedId);
    const g = analysis.graph;
    const near = hopDistances(g, g.index.get(selectedId)!, 2);
    const farTop = analysis.ranked.influence.find((id) => !near.has(g.index.get(id)!));
    add(farTop && `How could ${name} reach ${label(farTop)}?`);
    add(`Who are ${name}'s most valuable contacts, and why?`);
    const role: StructuralRole | undefined = analysis.metrics.get(selectedId)?.role;
    if (role === 'gatekeeper' || role === 'broker') add(`What would happen if ${name} left?`);
    else add(`What leverage does ${name} have in this network?`);
  }

  const topInfluencer = analysis.ranked.influence[0];
  add(topInfluencer && `Who could introduce us to ${label(topInfluencer)}?`);
  const gate = vulns.gatekeepers[0];
  add(gate && gate.cutOff > 0 && `What would happen if ${label(gate.id)} left?`);
  if (analysis.communities.length >= 3) add('Which clusters have no ties to each other?');
  else if (analysis.communities.length === 2) {
    add(`Who brokers between ${names.get(analysis.communities[0].id)} and ${names.get(analysis.communities[1].id)}?`);
  }
  const broker = analysis.ranked.brokerage[0];
  add(broker && broker !== topInfluencer && `Why is ${label(broker)} such an important broker?`);
  if (vulns.isolates.length || vulns.withoutRole.length || vulns.unscored.length) add('Where are the biggest intelligence gaps in this network?');
  add('Where is power concentrated, and who could shift it?');
  return out.slice(0, max);
}
